import { NavigateFunction } from 'react-router-dom';
import type { CostByInitiativeResponse } from '../../api/financials-client/financials-client.type';
import { DEFAULT_COLORS, UNCHECKED_COLOR } from '../allocation-of-costs/allocation-of-costs';
import { MAX_SELECTED_INITIATIVES } from './cost-by-initiative.constants';
import type { ShowCostByInitiativeChartBar } from './cost-by-initiative.type';

/**
 * Helper function to initialize the showCostByInitiativeChartBar state
 * @param data
 * @param setShowCostByInitiativeChartBar
 * @param setAvailableColors - Setter for the available colors state
 */
const initShowCostByInitiativeChartBar = (
  data: CostByInitiativeResponse[],
  setShowCostByInitiativeChartBar: React.Dispatch<React.SetStateAction<ShowCostByInitiativeChartBar[]>>,
  setAvailableColors: React.Dispatch<React.SetStateAction<string[]>>,
) => {
  const colorsToAssign = [...DEFAULT_COLORS]; // Use a fresh copy of default colors
  const initialShowBarState: ShowCostByInitiativeChartBar[] = [];

  // Sort data to assign colors to top initiatives first based on cost_to_date
  const sortedData = [...data].sort((a, b) => b.cost_to_date - a.cost_to_date);

  for (const item of sortedData) {
    const isEligible = item.cost_to_date > 0 && !item.name.toLowerCase().includes('non-initiative');
    let assignedColor = UNCHECKED_COLOR;
    let shouldShow = false;

    // Try to assign a color if the item is eligible and colors are available
    if (isEligible && colorsToAssign.length > 0) {
      assignedColor = colorsToAssign.pop()!; // Take a color from the end
      shouldShow = true;
    }

    initialShowBarState.push({
      id: item.id,
      show: shouldShow,
      color: assignedColor,
    });
  }

  setShowCostByInitiativeChartBar(initialShowBarState);
  // Update the parent component's availableColors state with whatever colors are left
  setAvailableColors(colorsToAssign);
};

/**
 * Helper function to update the showCostByInitiativeChartBar state when a checkbox is clicked
 * @param checked
 * @param id
 * @param _currentColorFromCheckbox - The color of the item at the moment of the click
 * @param availableColorsFromState - The current pool of available colors from component state
 * @param setShowError
 * @param setAvailableColors
 * @param setShowCostByInitiativeChartBar
 */
const updateShowCostByInitiativeChartBar = (
  checked: boolean,
  id: string,
  _currentColorFromCheckbox: string,
  availableColorsFromState: string[],
  setShowError: React.Dispatch<React.SetStateAction<boolean>>,
  setAvailableColors: React.Dispatch<React.SetStateAction<string[]>>,
  setShowCostByInitiativeChartBar: React.Dispatch<React.SetStateAction<ShowCostByInitiativeChartBar[]>>,
) => {
  setShowCostByInitiativeChartBar((prevShowBar) => {
    const currentlySelected = prevShowBar.filter((item) => item.show).length;

    if (!checked && currentlySelected <= 1) {
      return prevShowBar; // Prevent deselecting the last item
    }

    const newAvailableColors = [...availableColorsFromState];

    const newShowBar = prevShowBar.map((item) => {
      if (item.id === id) {
        if (checked) {
          // Selecting a new item
          if (currentlySelected >= MAX_SELECTED_INITIATIVES) {
            setShowError(true);
            return item; // Max items reached
          }
          if (newAvailableColors.length > 0) {
            setShowError(false);
            const assignedColor = newAvailableColors.pop()!; // Use pop for consistency
            return { ...item, show: true, color: assignedColor };
          }
          setShowError(true); // No colors available
          return item;
        } else {
          // Deselecting an item
          // item.color is the actual current color of this item in the state
          if (item.color !== UNCHECKED_COLOR) {
            newAvailableColors.push(item.color); // Return its color to the pool
          }
          return { ...item, show: false, color: UNCHECKED_COLOR };
        }
      }
      return item;
    });

    // Clear error once we’re below the limit
    if (newShowBar.filter((i) => i.show).length < MAX_SELECTED_INITIATIVES) {
      setShowError(false);
    }
    setAvailableColors(newAvailableColors);
    return newShowBar;
  });
};

/**
 * Helper function to navigate to the strategy page
 * @param navigate
 */
const onAddInitiativeClick = (navigate: NavigateFunction) => {
  const href = '/application/strategy';
  navigate(href);
};

/**
 * Counts the number of initiatives that are marked as shown.
 *
 * @param {ShowCostByInitiativeChartBar[]} showCostByInitiativeChartBar - Array of initiatives with visibility status.
 * @returns {number} The count of initiatives that are currently set to be displayed.
 */
const countSelectedInitiatives = (showCostByInitiativeChartBar: ShowCostByInitiativeChartBar[]): number => {
  return showCostByInitiativeChartBar.filter((item) => item.show).length;
};

/**
 * Determines if the maximum number of initiatives are selected.
 *
 * @param {ShowCostByInitiativeChartBar[]} showCostByInitiativeChartBar - Array of initiatives with visibility status.
 * @returns {boolean} True if the number of selected initiatives equals the maximum allowed (12), otherwise false.
 */
const isMaxSelected = (showCostByInitiativeChartBar: ShowCostByInitiativeChartBar[]): boolean => {
  return countSelectedInitiatives(showCostByInitiativeChartBar) === MAX_SELECTED_INITIATIVES;
};

export { initShowCostByInitiativeChartBar, isMaxSelected, onAddInitiativeClick, updateShowCostByInitiativeChartBar };
